import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
	Badge,
	Button,
	FontAwesome,
	ModalBoxCancel,
	ModalBoxPaymentMethodList,
	ModalBoxPaymentResult
} from 'Elements';
import { names } from 'Constants';
import {
	useAxios,
	useDelayUnmount,
	useModal,
	useSubscriptionPayments
} from 'Hooks';
import { Colors, PaymentStatus, Subscription, SubscriptionStatus } from 'Types';
import {
	agentPartnerApprovalStatus,
	api,
	clx,
	currencyFormatter,
	formatLocaleDateTime,
	getRemainingDay
} from 'Utils';
import styles from 'partials/card/subscription-card.module.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Props {
	subscription: Subscription;
	isLast?: boolean;
	isLoading?: boolean;
	showModal?: boolean;
	refetch?: () => void;
}

const Label = styled.label`
	@media (min-width: 1200px) {
		min-width: 60px;
	}
`;

export const SubscriptionCard: FC<Props> = ({
	subscription,
	isLast,
	showModal,
	refetch
}) => {
	const navigate = useNavigate();
	const [openModalCancel, toggleModalCancel] = useModal();
	const shouldRenderModalDelete = useDelayUnmount(openModalCancel, 350);
	const [openModal, toggleModal] = useModal();
	const [searchParams, setSearchParams] = useSearchParams();
	const [openModalResulte, toggleModalResulte] = useModal();
	const subscriptionPaymentStatusUrl =
		parseInt(searchParams.get('subscriptionPaymentStatus') as string) ?? -1;
	const [subscriptionPaymentStatus, setSubscriptionPaymentStatus] = useState(
		subscriptionPaymentStatusUrl ?? -1
	);
	const { subscriptions } = names;
	const { sendRequest } = useAxios<any>();
	const {
		sendRequest: sendSubscriptionRequest,
		isLoading: isSubmittingSubscription
	} = useAxios<string>();
	const { t } = useTranslation();
	const [subscriptionPayments, setSubscriptionPayments] = useState<any>(null);
	const [isLoadingPayments, setIsLoadingPayments] = useState<boolean>(true);
	const status = subscription?.status;
	const agent = subscription?.communityPartnerInfo;
	const navigateUrl = `${subscriptions?.path}/${subscription?.id}`;

	const getStatusData = () => {
		return agentPartnerApprovalStatus.find((elem) => elem.value === status);
	};

	const cardClass = clx(
		styles.card,
		isLast && styles.last,
		status === SubscriptionStatus.active && styles.paid,
		status === SubscriptionStatus.rejected && styles.rejected,
		status === SubscriptionStatus.pendingApproval && styles.pending,
		status === SubscriptionStatus.pendingPayment && styles.pending,
		status === SubscriptionStatus.expired && styles.expired
	);

	const onClickPay = async (subscriptionId) => {
		setSearchParams({ showModal: 'true', subscriptionId: subscriptionId });
	};

	const handleCancelSubscription = async () => {
		await sendSubscriptionRequest(
			api.cancelUpgradeSubscription(subscription?.id as string),
			{
				method: 'PUT'
			},
			async () => {
				refetch && refetch();
				toggleModalCancel();
			}
		);
	};

	const getPaymentData = async () => {
		if (subscription) {
			await sendRequest(
				api.getSubscriptionPaymentsDetail(
					subscription?.currentSubscriptionPaymentId
				),
				{ method: 'GET' },
				(paymentData) => setSubscriptionPayments(paymentData as any)
			);
		}
	};

	// Instead, call getPaymentData when subscription changes only (not isLoading)
	useEffect(() => {
		getPaymentData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription]);

	useEffect(() => {
		if (subscriptionPayments) {
			setIsLoadingPayments(false);
		}
	}, [subscriptionPayments]);

	useEffect(() => {
		if (
			showModal &&
			subscription?.status === SubscriptionStatus.pendingPayment
		) {
			if (subscriptionPaymentStatusUrl === PaymentStatus.InProcess) {
				setSubscriptionPaymentStatus(subscriptionPaymentStatusUrl);
				toggleModalResulte();
			} else {
				toggleModal();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscription, showModal]);

	const { data: subscriptionPaymentsUpgrade } = useSubscriptionPayments(
		subscription?.upgradeSubscriptionPaymentId ?? '',
		{
			enabled: !!subscription?.upgradeSubscriptionPaymentId
		}
	);

	// Wrap refetch to also call getPaymentData after refetch
	const handleRefetch = async () => {
		if (refetch) {
			await refetch();
			getPaymentData();
		}
	};

	return (
		<>
			<article className={cardClass}>
				<Row className="position-relative">
					<Col xl={6}>
						<div className="d-flex align-items-sm-center flex-column flex-sm-row gap-2 gap-sm-3 flex-wrap">
							<div
								className={clx(
									styles.col,
									`text-${getStatusData()?.color}`
								)}>
								<span className="fs-base fw-500">
									{subscription?.subscriptionPlanInfo?.name}
								</span>
								<span className="fs-base d-flex align-items-center">
									<span className="fw-500">
										{!isLoadingPayments ? (
											currencyFormatter(
												subscriptionPayments?.price
													?.totalAmount
													? subscriptionPayments
															?.price?.totalAmount
													: 0
											)
										) : (
											<div
												className={
													styles.skeletonLabel
												}></div>
										)}
									</span>
									<span className="text-gray-3 ms-2 fs-small">
										({t('label.tax-included')})
									</span>
								</span>
								{subscription?.subscriptionPlanInfo?.type ===
									1 && (
									<div>
										<Badge
											color={Colors.premium}
											className="gap-2">
											<FontAwesome
												icon={'medal'}
												size="lg"
											/>
											{t('button.premium')}
										</Badge>
									</div>
								)}
							</div>
							<div
								className={clx(
									styles.col,
									'text-sm-center text-xl-start flex-row flex-sm-column'
								)}>
								{status !== SubscriptionStatus.rejected && (
									<span className="fs-sm fw-400 color-gray-4">
										{status === SubscriptionStatus.active &&
											(subscription?.autoRenewal
												? t('title.renew-at')
												: t('title.expire-on'))}
										{(status ===
											SubscriptionStatus.pendingApproval ||
											status ===
												SubscriptionStatus.pendingPayment) &&
											t('title.expire-on')}
										{status ===
											SubscriptionStatus.expired &&
											t('title.expire-on')}
										:
									</span>
								)}
								<span className="fs-sm fw-500 color-gray-4 ps-2 ps-sm-0">
									{status === SubscriptionStatus.active &&
										(subscription?.renewDate
											? `${formatLocaleDateTime(
													subscription?.renewDate,
													false
											  )} (${getRemainingDay(
													new Date(),
													new Date(
														subscription?.renewDate
													)
											  )} ${t('label.days')})`
											: `${formatLocaleDateTime(
													subscription?.endDate,
													false
											  )} (${getRemainingDay(
													new Date(),
													new Date(
														subscription?.endDate
													)
											  )} ${t('label.days')})`)}
									{(status ===
										SubscriptionStatus.pendingApproval ||
										status === SubscriptionStatus.expired ||
										status ===
											SubscriptionStatus.pendingPayment) &&
										formatLocaleDateTime(
											subscription?.endDate,
											false
										)}
									{/* {status === SubscriptionStatus.expired &&
										formatLocaleDateTime(
											subscription?.purchaseDate ?? '',
											false
										)} */}
								</span>
							</div>
							<div className={styles.col}>
								<div className="fs-sm fw-400 color-gray-4 d-flex align-items-center justify-content-sm-end justify-content-xl-start">
									<Label>{t('label.status')}:</Label>
									<span
										className={clx(
											'fw-500 ps-2 flex-xl-grow-1',
											`text-${getStatusData()?.color}`
										)}>
										{t(`${getStatusData()?.label}`)}
									</span>
								</div>
							</div>
						</div>
					</Col>
					<Col xl={6}>
						<div className="mt-4 mt-xl-0 d-flex align-items-center h-lg-100 flex-wrap gap-3 gap-sm-2">
							{agent && (
								<div className="d-flex align-items-center flex-grow-1 gap-2">
									{/* <img
										src={agent.logo || defaultImage}
										alt={agent.organizationName}
										className={clx(
											styles.logo,
											styles.logoMd
										)}
									/> */}
									<FontAwesome icon="apartment" size="lg" />
									<span className="fs-sm fw-400">
										<div>{agent.organizationName}</div>
									</span>
								</div>
							)}
							<div className="d-flex align-items-center gap-2">
								{subscription?.status === 3 &&
									(subscriptionPayments?.status ===
										PaymentStatus.Pending ||
										subscriptionPayments?.status ===
											PaymentStatus.Failed) && (
										<Button
											onClick={() => {
												onClickPay(subscription?.id);
											}}
											size="sm"
											className="px-4"
											label={t('button.pay') as string}
										/>
									)}
								<Button
									as="a"
									to={navigateUrl}
									state={{
										prevPath: subscriptions.path
									}}
									size="sm"
									color={Colors['white-primary']}
									label={t('button.details') as string}
								/>
							</div>
						</div>
					</Col>
				</Row>
				<Row className="position-relative mt-4">
					{subscription?.upgradeSubscriptionPaymentId && (
						<div
							className="d-flex flex-wrap align-items-center justify-content-between gap-2 text-yellow py-2 px-4"
							style={{
								borderRadius: '16px',
								background: '#FFFFFFD9'
							}}>
							<div className="d-flex align-items-center gap-2">
								<FontAwesome icon="circle-info" />
								<span className="fw-500">
									{t(
										'label.your-upgrade-process-has-been-complete'
									)}
									.
								</span>
							</div>
							<div className="d-flex flex-wrap align-items-center gap-2">
								<Button
									size="sm"
									color={Colors['border-yellow']}
									onClick={() => {
										navigate(
											`/subscriptions/${subscription?.id}/upgrade?step=step2&communityPartnerId=${subscription?.communityPartnerInfo?.communityPartnerId}&CurrentSubscriptionPlanId=${subscription?.subscriptionPlanInfo?.subscriptionPlanId}&UpgradeSubscriptionPlanId=${subscriptionPaymentsUpgrade?.subscriptionPlanInfo?.subscriptionPlanId}&planType=${subscriptionPaymentsUpgrade?.subscriptionPlanInfo?.type}`,
											{ replace: true }
										);
									}}>
									{t('button.continue-process')}
								</Button>
								<Button
									size="sm"
									color={Colors['border-red']}
									onClick={toggleModalCancel}>
									{t('button.cancel-subscription-upgrade')}
								</Button>
							</div>
						</div>
					)}
				</Row>
			</article>
			<ModalBoxPaymentMethodList
				subscriptionId={subscription?.id}
				urlBack="/subscriptions?"
				isVoucher={true}
				returnUrl={`subscriptions?subscriptionId=${subscription?.id}&showModal=true&`}
				totalAmount={
					subscriptionPayments?.price?.totalAmount
						? subscriptionPayments?.price?.totalAmount
						: 0
				}
				subscriptionDuration={
					subscriptionPayments?.price?.subscriptionDuration
				}
				currentSubscriptionPaymentId={
					subscription?.currentSubscriptionPaymentId
				}
				voucherCodeCurrent={subscriptionPayments?.price?.voucherCode}
				selectedPlanId={
					subscriptionPayments?.subscriptionPlanInfo
						?.subscriptionPlanId
				}
				communityPartnerId={
					subscription?.communityPartnerInfo
						?.communityPartnerId as any
				}
				selectedPlanType={
					subscriptionPayments?.subscriptionPlanInfo?.type
				}
				isOpen={openModal}
				refetch={handleRefetch}
				toggleModalResulte={toggleModalResulte}
				onClose={toggleModal}
				subscriptionPaymentStatus={subscriptionPaymentStatus}
				setSubscriptionPaymentStatus={setSubscriptionPaymentStatus}
			/>
			<ModalBoxPaymentResult
				subscriptionId={subscription?.id}
				subscriptionCalculate={{
					totalAmount: subscriptionPayments?.price?.totalAmount
						? subscriptionPayments?.price?.totalAmount
						: 0,
					subscriptionDuration:
						subscriptionPayments?.price?.subscriptionDuration
				}}
				subscriptionPaymentStatus={subscriptionPaymentStatus}
				isOpen={openModalResulte}
				onClose={toggleModalResulte}
			/>
			{shouldRenderModalDelete && (
				<ModalBoxCancel
					isOpen={openModalCancel}
					onClose={toggleModalCancel}
					onCancel={handleCancelSubscription}
					isRemoving={isSubmittingSubscription}
					title={subscription?.subscriptionPlanInfo?.name ?? ''}
				/>
			)}
		</>
	);
};
